import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { BaseComponent } from '../../shared/models/base-component';
import { RepairShopAddress, RepairShopService } from '../../shared/services/repair-shop.service';

interface RepairShopFacility {
  address: {
    lineOne?: string;
    city?: string;
    administrativeAreaLevelOne?: string;
    postalCode?: string;
    country?: string;
    coordinates?: { latitude: number; longitude: number };
  };
  companyId?: string;
  contact?: {
    emails?: string[];
    phones?: Array<{ number?: string; primary?: boolean }>;
  };
  distance?: {
    driveTime?: number;
    units?: string;
    value?: number;
  };
  name?: string;
  properties?: {
    nationwideShopID?: string;
    [key: string]: any;
  };
}

interface RepairShopParticipant {
  address: string;
  city: string;
  country: string;
  nationwideShopID: string;
  companyId: string;
  phoneNumber: string;
  proximateDistance: number;
  shopName: string;
  state: string;
  zipCode: string;
  latitude?: number;
  longitude?: number;
  serialNumber?: number;
  driveTime?: number;
}

export const USER_TYPES = { CUSTOMER: 'customer' } as const;
export const USER_SOURCES = {
  PUBLIC_WEB: 'public_web',
  AUTHENTICATED_WEB: 'authenticated_web'
} as const;

@Component({
  selector: 'is-search-bar',
  templateUrl: './search-bar.component.html',
  styleUrls: ['./search-bar.component.scss'],
  standalone: false
})
export class SearchBarComponent extends BaseComponent implements OnInit, OnChanges, OnDestroy {
  private readonly PAGE_SIZE = 10;
  @Input() accessToken!: string;
  @Input() addressLoaded = false;
  @Input() claimId?: string;
  @Input() externalClaimId?: string;
  @Input() vehicleIncidentId?: string;
  @Input() clientId!: string;
  @ViewChild('bringFocus') private bringFocus!: ElementRef;
  @Input() currentLocation!: string;
  @Input() defaultSearchLocation!: RepairShopAddress;
  @Input() env!: string;
  @Input() selectedYear = '';
  @Input() selectedMake = '';
  @Input() selectedModel = '';
  @Input() vehicleForm?: FormGroup;
  @Output() loadAddress = new EventEmitter<boolean>();
  @Output() onError = new EventEmitter<any>();
  @Output() shopDetails = new EventEmitter();
  @Input() userTypeValue!: boolean;
  addressLineSuggestions: string[] = [];
  addressZipCode: string[] = [];
  completeRepairShopData!: RepairShopParticipant[];
  completeRepairShopsList!: RepairShopParticipant[];
  errorMessage = '';
  isError = false;
  isShopLoading = false;
  label!: string;
  repairShopsList: RepairShopParticipant[] = [];
  selectedAddress!: string;
  selectedListPage = 0;
  shopId!: number;
  showLoader = false;
  suggestedAddressess: RepairShopAddress[] = [];
  private lastExecutedSearchKey = '';
  private readonly destroy$ = new Subject<void>();

  constructor(private repairShopService: RepairShopService) {
    super();
  }

  private mapFacilityToParticipant(facility: RepairShopFacility, serialNumber: number): RepairShopParticipant {
    const primaryPhone = facility.contact?.phones?.find((p) => p.primary) || facility.contact?.phones?.[0];
    return {
      address: facility.address?.lineOne || '',
      city: facility.address?.city || '',
      state: facility.address?.administrativeAreaLevelOne || '',
      zipCode: facility.address?.postalCode || '',
      country: facility.address?.country || '',
      shopName: facility.name || '',
      nationwideShopID: facility.properties?.nationwideShopID || facility.companyId || '',
      companyId: facility.companyId || '',
      phoneNumber: primaryPhone?.number || '',
      proximateDistance: facility.distance?.value || 0,
      driveTime: facility.distance?.driveTime || 0,
      latitude: facility.address?.coordinates?.latitude,
      longitude: facility.address?.coordinates?.longitude,
      serialNumber
    };
  }

  errorHandling(type?: string): void {
    this.isShopLoading = false;
    this.showLoader = false;
    this.isError = true;
    switch (type) {
      case 'input':
        this.errorMessage = 'Invalid Address';
        break;
      case 'noResult':
        this.errorMessage = 'No Shops Found';
        break;
      default:
        this.errorMessage = 'Search is unavailable. Please try again later.';
        break;
    }
  }

  getAddressDetails(event: any): void {
    this.selectedAddress = event?.target?.value;
    this.isError = false;
    this.errorMessage = '';
    for (let suggestedAddressIndex = 0; suggestedAddressIndex < this.suggestedAddressess?.length; suggestedAddressIndex++) {
      if (this.selectedAddress === this.suggestedAddressess[suggestedAddressIndex].zipCode) {
        this.getRepairShopList(this.suggestedAddressess[suggestedAddressIndex]);
        return;
      }
    }
    // fallback: no zip match — try matching by full address text index
    const matchIndex = this.addressZipCode.indexOf(this.selectedAddress);
    if (matchIndex >= 0) {
      this.getRepairShopList(this.suggestedAddressess[matchIndex]);
      return;
    }
    this.repairShopsList = [];
    this.errorHandling('input');
  }

  getAddressToSearch(): RepairShopAddress | undefined {
    if (this.defaultSearchLocation) {
      const { addressLine1, city, state, zipCode, country } = this.defaultSearchLocation;
      this.selectedAddress = [addressLine1, city, state, zipCode, country].filter(Boolean).join(', ');
      return this.defaultSearchLocation;
    }
    this.selectedAddress = this.currentLocation || '';
    return this.selectedAddress ? this.repairShopService.parseAddress(this.selectedAddress) : undefined;
  }

  getDirections(shop: RepairShopParticipant): void {
    const destination = `${shop.address.replace(/#(\S*)/g, '')}, ${shop.city}, ${shop.state}, ${shop.zipCode}`;
    const params = new URLSearchParams({
      saddr: this.selectedAddress,
      daddr: destination
    });
    window.open(`https://maps.google.com/?${params.toString()}`, '_blank');
  }

  formatDriveTime(driveTime?: number): string {
    const totalMinutes = Math.max(1, Math.ceil(driveTime || 0));
    if (totalMinutes < 60) {
      return `${totalMinutes} min drive`;
    }

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (minutes === 0) {
      return `${hours} hr drive`;
    }

    return `${hours} hr ${minutes} min drive`;
  }

  getRepairShopList(selectedAddress?: RepairShopAddress): void {
    if (!this.canSearch()) {
      return;
    }
    this.updateLastExecutedSearchKey(selectedAddress);
    this.repairShopsList = [];
    this.completeRepairShopData = [];
    this.selectedListPage = 0;
    this.isShopLoading = true;
    this.shopDetails.emit(null);
    this.repairShopService.buildGetOYSRepairShop(this.buildSearchRequest(selectedAddress)).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response: any) => {
        this.selectedAddress = this.repairShopService.getSelectedAddress(selectedAddress) || '';
        const facilities: RepairShopFacility[] = response?.facilities || [];
        if (facilities.length > 0) {
          this.completeRepairShopData = facilities.map((facility: RepairShopFacility, index: number) => this.mapFacilityToParticipant(facility, index + 1));
          this.completeRepairShopsList = this.completeRepairShopData;
          this.repairShopsList = this.completeRepairShopData.slice(this.selectedListPage, this.selectedListPage + this.PAGE_SIZE);
          this.addressLoaded = true;
          this.loadAddress.emit(this.addressLoaded);
          this.showLoader = false;
          this.isShopLoading = false;
          this.isError = false;
        } else {
          this.errorHandling('noResult');
          this.onError.emit({ errorMessage: 'No Results' } as any);
        }
      },
      error: (error: HttpErrorResponse) => {
        this.errorHandling();
        this.onError.emit(error);
      }
    });
  }

  private buildSearchRequest(selectedAddress?: RepairShopAddress): any {
    const vehicleSearchFields: any = {};
    if (this.selectedYear) {
      vehicleSearchFields.year = this.selectedYear;
    }
    if (this.selectedMake) {
      vehicleSearchFields.make = this.selectedMake;
    }
    if (this.selectedModel) {
      vehicleSearchFields.model = this.selectedModel;
    }
    return {
      ...(selectedAddress || {}),
      ...vehicleSearchFields,
      externalClaimId: this.externalClaimId || null,
      claimId: this.claimId || null,
      userType: USER_TYPES.CUSTOMER,
      userSource: this.userTypeValue ? USER_SOURCES.AUTHENTICATED_WEB : USER_SOURCES.PUBLIC_WEB,
      vehicleIncidentId: this.vehicleIncidentId || null
    };
  }

  ngOnInit(): void {
    this.label = this.userTypeValue ? 'Enter an address or ZIP Code. Then pick from the results.' : 'Enter an address or ZIP Code';
    const address = this.getAddressToSearch();
    if (address && this.canSearch()) this.getRepairShopList(address);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.userTypeValue) {
      return;
    }

    const vehicleInputsChanged = !!(changes['selectedYear'] || changes['selectedMake'] || changes['selectedModel'] || changes['vehicleForm']);
    if (!vehicleInputsChanged) {
      return;
    }

    this.tryAutoSearchWhenReady();
  }

  onPageClick(i: number): void {
    this.repairShopsList = [];
    this.isShopLoading = true;
    this.selectedListPage = i;
    this.repairShopsList = this.completeRepairShopsList.slice(this.selectedListPage * this.PAGE_SIZE, (this.selectedListPage + 1) * this.PAGE_SIZE);
    this.isShopLoading = false;
    this.bringFocus?.nativeElement.focus();
  }

  paginationCounter(): any[] {
    return Array(Math.ceil(this.completeRepairShopsList.length / this.PAGE_SIZE)).fill('');
  }


  getTypeAheadCall(searchTerm: any, event: any): any {
    this.repairShopService.getTypeAheadSuggestions(searchTerm).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response: any) => {
        this.isError = false;
        this.showLoader = false;
        if (response.response?.length) {
          const suggestedAddresses = response.response.map((address: any) => ({
            addressLine1: address.addressLine || '',
            city: address.city || '',
            state: address.state || '',
            zipCode: address.zip || '',
            country: address.country === 'USA' ? 'US' : (address.country || '')
          }));
          this.suggestedAddressess = suggestedAddresses;
          this.addressLineSuggestions = [];
          this.addressZipCode = [];
          this.suggestedAddressess.forEach((suggestedAddress: RepairShopAddress) => {
            if (suggestedAddress.country === 'US') {
              let suggestedText = `${suggestedAddress.addressLine1}~${suggestedAddress.city || ''}~${suggestedAddress.state || ''}~${suggestedAddress.zipCode || ''}~${suggestedAddress.country || ''}`;
              suggestedText = suggestedText.replace(/~+/g, ', ');
              this.addressLineSuggestions.push(suggestedText);
              this.addressZipCode.push(suggestedAddress?.zipCode || '');
            }
          });
        } else {
          this.addressLineSuggestions = [];
          this.completeRepairShopData = [];
        }
        event.target.optionvalues = this.addressZipCode;
        event.target.options = this.addressLineSuggestions;
      },
      error: (error: HttpErrorResponse) => {
        this.showLoader = false;
        this.errorHandling();
        this.onError.emit(error);
      }
    });
  }

  setOptions(event: any): void {
    const searchTerm = event.target.value;
    this.isError = false;
    if (searchTerm.length === 5 && parseInt(searchTerm, 10) < 100000) {
      this.showLoader = true;
      this.zipCodeHandling(searchTerm, event);
    } else if (searchTerm.length >= 3) {
      this.showLoader = true;
      this.getTypeAheadCall(searchTerm, event);
    }
  }

  setShopData(shop: any): void {
    this.shopId = shop.nationwideShopID;
    this.shopDetails.emit(shop);
  }

  zipCodeHandling(searchTerm: string, event: any): void {
    event.target.options = [];
    this.selectedAddress = searchTerm;
    if (!this.canSearch()) {
      this.showLoader = false;
      this.isShopLoading = false;
      return;
    }
    this.getRepairShopList({ zipCode: searchTerm });
    this.addressLineSuggestions = [];
    this.addressZipCode = [];
  }

  private tryAutoSearchWhenReady(): void {
    const address = this.getCurrentAddressToSearch();
    if (!address || !this.canSearch(false)) {
      return;
    }

    const nextSearchKey = this.buildSearchKey(address);
    if (nextSearchKey === this.lastExecutedSearchKey) {
      return;
    }

    this.getRepairShopList(address);
  }

  private getCurrentAddressToSearch(): RepairShopAddress | undefined {
    if (this.defaultSearchLocation) {
      return this.defaultSearchLocation;
    }

    const searchAddress = (this.selectedAddress || this.currentLocation || '').trim();
    if (!searchAddress) {
      return undefined;
    }

    if (/^\d{5}$/.test(searchAddress)) {
      return { zipCode: searchAddress };
    }

    return this.repairShopService.parseAddress(searchAddress);
  }

  private buildSearchKey(selectedAddress?: RepairShopAddress): string {
    return JSON.stringify(this.buildSearchRequest(selectedAddress));
  }

  private canSearch(markTouched: boolean = true): boolean {
    if (!this.vehicleForm) {
      return true;
    }

    if (this.vehicleForm.valid) {
      return true;
    }

    if (markTouched) {
      this.vehicleForm.markAllAsTouched();
    }
    return false;
  }

  private updateLastExecutedSearchKey(selectedAddress?: RepairShopAddress): void {
    this.lastExecutedSearchKey = this.buildSearchKey(selectedAddress);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
